import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useProfile } from '../hooks/useProfile';
import { supabase } from '../lib/supabase';
import { Title } from '../components/ui/Title';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { Grid } from '../components/ui/Grid';
import { Heart, Video, Music, FileText, Image, Download, ExternalLink, Lock } from 'lucide-react';
import { SEOHead } from '../components/SEOHead';
import type { Band, ExclusiveContent, FanSubscription } from '../types/database';

function FanContent() {
  const { t } = useTranslation();
  const { bandSlug } = useParams<{ bandSlug: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { profile } = useProfile();
  const [band, setBand] = useState<Band | null>(null);
  const [content, setContent] = useState<ExclusiveContent[]>([]);
  const [subscription, setSubscription] = useState<FanSubscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function loadFanContent() {      
      if (!bandSlug) {
        setError('Band not found');
        setLoading(false);
        return;
      }

      try {
        // Get band details
        const { data: bandData, error: bandError } = await supabase
          .from('bands')
          .select('*')
          .eq('slug', bandSlug)
          .single();

        if (bandError) throw bandError;
        if (!bandData) throw new Error('Band not found');

        setBand(bandData);

        // If user is not logged in, show login required message
        if (!user) {
          setError('You must be logged in to access fan content');
          setLoading(false);
          return;
        }

        console.log('FanContent: Checking subscription for auth user:', user.id, 'band:', bandData.id);
        
        // Get user's profile first
        const { data: userProfile, error: profileError } = await supabase
          .from('profiles')
          .select('id')
          .eq('user_id', user.id)
          .single();

        if (profileError || !userProfile) {
          setError('Please complete your profile to access fan content');
          setLoading(false);
          return;
        }

        console.log('FanContent: Checking subscription for profile:', userProfile.id, 'band:', bandData.id);
        const { data: subscriptionData, error: subscriptionError } = await supabase
          .from('fan_subscriptions')
          .select('*')
          .eq('user_id', userProfile.id)
          .eq('band_id', bandData.id)
          .in('status', ['completed', 'active'])
          .maybeSingle();

        if (subscriptionError) throw subscriptionError;
        console.log('FanContent: Subscription found:', !!subscriptionData, 'Status:', subscriptionData?.status);

        if (!subscriptionData) {
          setError('You need to be a fan of this band to access exclusive content');
          setLoading(false);
          return;
        }

        setSubscription(subscriptionData);

        // Load exclusive content
        console.log('FanContent: Loading exclusive content for subscriber');
        const { data: contentData, error: contentError } = await supabase
          .from('exclusive_content')
          .select('*')
          .eq('band_id', bandData.id)
          .order('created_at', { ascending: false });

        if (contentError) throw contentError;
        console.log('FanContent: Loaded content for subscriber:', contentData?.length, 'items');
        setContent(contentData || []);
        
        // Set loading to false here since we have valid access
        setLoading(false);
      } catch (err) {
        console.error('Error loading fan content:', err);
        setError(err instanceof Error ? err.message : 'Failed to load content');
        setLoading(false);
      }
    }

    loadFanContent();
  }, [bandSlug, user, profile]);

  const getContentIcon = (type: string) => {
    switch (type) {
      case 'video':
        return <Video className="w-6 h-6" />;
      case 'audio':
        return <Music className="w-6 h-6" />;
      case 'image':
        return <Image className="w-6 h-6" />;
      case 'file':
        return <Download className="w-6 h-6" />;
      case 'link':
        return <ExternalLink className="w-6 h-6" />;
      default:
        return <FileText className="w-6 h-6" />;
    }
  };

  const handleContentClick = (contentItem: ExclusiveContent) => {
    if (contentItem.type === 'link') {
      window.open(contentItem.content_url, '_blank');
    } else {
      // For other types, you might want to handle differently
      // For now, we'll just open the URL
      window.open(contentItem.content_url, '_blank');
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-6 py-24">
        <div className="text-center text-light/60">Loading exclusive content...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-6 py-24">
        <div className="text-center">
          <Lock className="w-16 h-16 text-light/40 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-light mb-4">Access Restricted</h2>
          <p className="text-light/60 mb-6">{error}</p>
          <button
            onClick={() => navigate(`/bands/${bandSlug}`)}
            className="btn-primary"
          >
            Back to Band Page
          </button>
        </div>
      </div>
    );
  }

  if (!band) {
    return (
      <div className="container mx-auto px-6 py-24">
        <div className="text-center text-red-500">Band not found</div>
      </div>
    );
  }

  const breadcrumbItems = [
    { label: t('nav.bands'), href: '/bands' },
    { label: band.name, href: `/bands/${bandSlug}` },
    { label: 'Fan Content' },
  ];

  return (
    <>
      <SEOHead
        title={`${band.name} - Exclusive Fan Content`}
        description={`Exclusive content for ${band.name} fans. Access premium videos, music, and behind-the-scenes content.`}
        keywords={[band.name, 'exclusive content', 'fan area', 'premium content']}
        url={`https://jamnation.ch/bands/${bandSlug}/fan-content`}
        noIndex={true}
      />

      <div className="container mx-auto px-6 pb-8 md:py-24">
        <div className="mb-6">
          <Breadcrumbs items={breadcrumbItems} />
        </div>

        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="panel mb-8">
            <div className="flex items-center space-x-4">
              <div>
                <h1 className="text-3xl font-bold text-light">{band.name} {t('fanArea.fanContent')}</h1>
                <p className="text-light/60">{t('fanArea.exclusiveContentDescription')}</p>
              </div>
            </div>
          </div>

          {/* Content Grid */}
          {content.length > 0 ? (
            <Grid cols={3}>
              {content.map((item) => (
                <div
                  key={item.id}
                  onClick={() => handleContentClick(item)}
                  className="panel hover:bg-gray-custom/20 cursor-pointer transition-all duration-300 hover:scale-105"
                >
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-12 h-12 bg-primary/20 rounded-lg flex items-center justify-center text-primary">
                      {getContentIcon(item.type)}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-light">{item.title}</h3>
                      <p className="text-sm text-light/60 capitalize">{item.type}</p>
                    </div>
                  </div>

                  {item.description && (
                    <p className="text-light/80 text-sm mb-4 line-clamp-3">
                      {item.description}
                    </p>
                  )}

                  <div className="flex items-center justify-between text-sm text-light/60">
                    <span>{new Date(item.created_at).toLocaleDateString()}</span>
                    <ExternalLink className="w-4 h-4" />
                  </div>
                </div>
              ))}
            </Grid>
          ) : (
            <div className="text-center py-12 panel">
              <Heart className="w-16 h-16 text-light/40 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-light mb-2">{t('fanArea.noContentYet')}</h3>
              <p className="text-light/60">
                {t('fanArea.checkBackSoon')}
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default FanContent;